import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import BlockWrapper from '@components/hyperDashboard/blocks/BlockWrapper';
import { selectSsapDataInconsistencyIssues } from '@utils/ssap/ssapDataInconsistency/ssapDataInconsistencySelectors';
import { groupIssuesByRule } from '@utils/ssap/ssapDataInconsistency/groupIssuesByRule';

import { darculaDataInconsistenciesInfoStyles } from '../darculaTheme';

const {
    blockWrapperAdditionalStyles,
    titleBlockStyle,
    emphasisValueStyle,
    listContainerStyle,
    ruleGroupStyle,
    ruleHeaderButtonStyle,
    ruleToggleGlyphStyle,
    ruleDescriptionStyle,
    ruleIssuesContainerStyle,
    issueRowStyle,
    issueRowLastStyle,
    issueMessageStyle,
    pathStyle,
} = darculaDataInconsistenciesInfoStyles;

const DataInconsistenciesInfo = ({ id }) => {
    const issues = useSelector(selectSsapDataInconsistencyIssues);
    const groupedRules = useMemo(() => groupIssuesByRule(issues), [ issues ]);
    const [ collapsedByRule, setCollapsedByRule ] = useState({});

    const toggleRuleGroup = ruleId => {
        setCollapsedByRule(state => ({
            ...state,
            [ruleId]: !state[ruleId],
        }));
    };

    const renderIssue = (issue, index, isLast) => (
        <div
            key={ `${issue.ruleId}-${index}` }
            style={ isLast ? issueRowLastStyle : issueRowStyle }
        >
            <div style={ issueMessageStyle }>{ issue.message }</div>
            {
                issue.path ?
                    <div style={ pathStyle }>
                        Path: { issue.path.join(' → ') }
                    </div> :
                    null
            }
        </div>
    );

    const renderRuleGroup = group => {
        const isCollapsed = collapsedByRule[group.ruleId] ?? false;

        return (
            <div key={ group.ruleId } style={ ruleGroupStyle }>
                <button
                    type="button"
                    style={ ruleHeaderButtonStyle }
                    aria-expanded={ !isCollapsed }
                    onClick={ () => toggleRuleGroup(group.ruleId) }
                >
                    <span style={ ruleToggleGlyphStyle }>{ isCollapsed ? '+' : '−' }</span>
                    <span>
                        { group.title }
                        { ` (${group.issues.length})` }
                    </span>
                </button>
                {
                    !isCollapsed && (
                        <>
                            {
                                group.description ?
                                    <div style={ ruleDescriptionStyle }>{ group.description }</div> :
                                    null
                            }
                            <div style={ ruleIssuesContainerStyle }>
                                {
                                    group.issues.map((issue, index) =>
                                        renderIssue(issue, index, index === group.issues.length - 1),
                                    )
                                }
                            </div>
                        </>
                    )
                }
            </div>
        );
    };

    return (
        <BlockWrapper id={ id } additionalStyles={ blockWrapperAdditionalStyles }>
            <div style={ titleBlockStyle }>
                SSAP data inconsistencies
                <br />
                Found: &nbsp;
                <strong style={ emphasisValueStyle }>{ issues.length }</strong>
                {
                    groupedRules.length > 1 ?
                        <>
                            { ' · ' }
                            { groupedRules.length }
                            { ' rules' }
                        </> :
                        null
                }
            </div>
            <div style={ listContainerStyle }>
                {
                    issues.length === 0 ?
                        <div style={ issueMessageStyle }>No inconsistencies detected in current application data.</div> :
                        groupedRules.map(renderRuleGroup)
                }
            </div>
        </BlockWrapper>
    );
};

export default DataInconsistenciesInfo;
